import { useTranslation } from "react-i18next";
import { Download, HelpCircle } from "lucide-react";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { StatusBadge } from "@/shared/ui/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table";
import type { CsUnmatchedQuestion } from "../../api/aiKefuService";
import { UNMATCHED_STATUS_LABELS } from "./constants";

type Props = {
  /** 当前页待处理(open)问题;调用方已切片。 */
  questions: CsUnmatchedQuestion[];
  /** 全量 open 总数(徽标/导出可用性用;缺省回退到当前页条数)。 */
  totalOpen?: number;
  onUpdateStatus: (id: number, status: string) => void;
  onExport: () => void;
};

/**
 * 未命中问题看板(B2 诊断驱动知识缺口闭环)。
 *
 * 设计决策(用户拍板):**不做 promote-to-kb**(KB 单一来源走文档上传,不开第二写入口)。运营据此清单 →
 * 整理成 QA 文档 → 走正常上传流程补 KB。命中后自动闭环(后端 resolveOnHit),这里只列 + 改状态 + 导出。
 */
export function UnmatchedCard({ questions, totalOpen, onUpdateStatus, onExport }: Props) {
  const { t } = useTranslation("customer-service");
  // 已处理(resolved/ignored)从清单移除,只展示待处理 open;点「已解决/忽略」后该行 refetch 即消失。
  // 导出仍走全量(onExport → 后端 CSV 端点,不受此显示过滤影响)。
  const openQuestions = questions.filter((q) => q.status === "open");
  // 徽标显示全量 open 总数(分页时当前页只是切片);缺省回退当前页。
  const openCount = totalOpen ?? openQuestions.length;

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="section-title flex items-center gap-2">
            <HelpCircle className="h-4 w-4 text-warning" />
            {t("unmatched.title")}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-2xs">
              {t("unmatched.countBadge", { count: openCount })}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              className="text-xs"
              disabled={openCount === 0}
              onClick={onExport}
            >
              <Download className="h-3.5 w-3.5 mr-1" />
              {t("unmatched.exportAction")}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-2xs text-muted-foreground">{t("unmatched.hint")}</p>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("unmatched.headerQuestion")}</TableHead>
              <TableHead>{t("unmatched.headerHitCount")}</TableHead>
              <TableHead>{t("unmatched.headerStatus")}</TableHead>
              <TableHead>{t("unmatched.headerActions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {openQuestions.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">
                  {t("unmatched.empty")}
                </TableCell>
              </TableRow>
            )}
            {openQuestions.map((q, i) => (
              <TableRow
                key={q.id}
                className="fx-enter"
                style={{ animationDelay: `${Math.min(i, 7) * 30}ms` }}
              >
                <TableCell className="text-sm">{q.question}</TableCell>
                <TableCell className="font-medium">{q.hitCount}</TableCell>
                <TableCell>
                  <StatusBadge
                    type={
                      q.status === "open" ? "error" : q.status === "resolved" ? "success" : "info"
                    }
                  >
                    {UNMATCHED_STATUS_LABELS[q.status] ?? "未知状态"}
                  </StatusBadge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      disabled={q.status === "resolved"}
                      onClick={() => onUpdateStatus(q.id, "resolved")}
                    >
                      {t("unmatched.resolvedAction")}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      disabled={q.status === "ignored"}
                      onClick={() => onUpdateStatus(q.id, "ignored")}
                    >
                      {t("unmatched.ignoredAction")}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
